import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Typography } from '@material-ui/core'
import { Loader, Button } from 'sputnik-ui'
import { useTranslation } from 'react-i18next'
import trackGoal from 'utils/metrika'

import {
  fetchAllCameras,
  selectGroupedByAddress,
  selectPagination,
  selectFilters,
  clearCamerasView,
} from 'features/CamerasView/camerasViewSlice'

import CamerasHeader from './CamerasHeader'
import CamerasGroup from './CamerasGroup'

const PageWrapper = styled.div`
  padding: 32px 40px;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
`

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0 48px;
`

function CamerasPage() {
  const dispatch = useDispatch()
  const { t } = useTranslation(['cameras_view'])
  const groups = useSelector(selectGroupedByAddress)
  const filters = useSelector(selectFilters)
  const { page, totalPages, total, pending } = useSelector(selectPagination)
  const lastSearchSignatureRef = useRef('')

  useEffect(() => {
    trackGoal('control_cameras_open_list')
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(fetchAllCameras({ page: 1, limit: 50, search: filters.search }))
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [dispatch, filters.search])

  useEffect(
    () => () => {
      dispatch(clearCamerasView())
    },
    [dispatch]
  )

  useEffect(() => {
    const normalizedSearch = filters.search.trim()
    if (normalizedSearch.length < 3 || pending) return

    const signature = `${normalizedSearch}:${total}:${filters.status || 'all'}`
    if (lastSearchSignatureRef.current === signature) return

    lastSearchSignatureRef.current = signature
    trackGoal('control_cameras_search', {
      query_length: normalizedSearch.length,
      results_count: total,
      status: filters.status || 'all',
    })
  }, [filters.search, filters.status, pending, total])

  const handleLoadMore = () => {
    if (page < totalPages && !pending) {
      trackGoal('control_cameras_load_more', {
        page: page + 1,
        status: filters.status || 'all',
        has_search: filters.search.trim().length >= 3,
      })
      dispatch(
        fetchAllCameras({ page: page + 1, limit: 50, search: filters.search })
      )
    }
  }

  const hasMore = page < totalPages

  if (pending && total === 0) {
    return (
      <PageWrapper>
        <CamerasHeader />
        <Loader
          fullPage
          style={{ justifyContent: 'center', display: 'flex' }}
        />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <CamerasHeader />

      {groups.length === 0 ? (
        <EmptyState>
          <Typography color="textSecondary" variant="h6">
            {t('cameras_view:no_cameras')}
          </Typography>
        </EmptyState>
      ) : (
        <>
          {groups.map((group) => (
            <CamerasGroup key={group.key} group={group} />
          ))}

          {hasMore && (
            <LoadMoreWrapper>
              <Button secondary onClick={handleLoadMore} disabled={pending}>
                {pending ? '...' : t('cameras_view:load_more')}
              </Button>
            </LoadMoreWrapper>
          )}
        </>
      )}
    </PageWrapper>
  )
}

export default CamerasPage
